/**
 * Coachmark popover (PEL-516).
 *
 * The single reusable coachmark surface. Every coachmark shares the same chrome
 * so they read as one "Pelgo Coach" voice:
 *  - a constant header (sparkle + "Pelgo Coach" + separator),
 *  - a contextual body, fully rendered from the first frame (no typing),
 *  - contextual action buttons (primary / secondary link / multi-step controls),
 *    rendered only when the step defines them.
 *
 * Tint, outline and type match the in-app coach strip (the "Generate a tailored
 * ATS resume…" card): the sky palette, light type, no bold weights.
 *
 * Motion (spec §9): the whole card enters as a single-shot "pop" — scale
 * 0.8 -> 1 with one subtle overshoot, opacity 0 -> 1 (~250ms, WhatsApp message
 * feel); it leaves with a quick scale 1 -> 0.96 + fade (~140ms, no bounce), and
 * on a step->step advance the current card exits before the next pops in. The pop
 * runs on an INNER wrapper so floating-ui keeps ownership of the outer `translate`
 * (positioning / anchor-tracking) and the two transforms never fight. The typing
 * animation was removed 2026-06-23 (the shared `useCoachTyping` hook stays in
 * place for the in-app chat coach strip — this only stops consuming it here).
 * `prefers-reduced-motion` drops the scale/bounce entirely (instant, no defer).
 *
 * Anchored by floating-ui (offset + flip + shift + arrow + autoUpdate) so it
 * handles viewport edges, scrolling, and anchor remounts. Pure consumer of an
 * `ActiveFtueStep` from the engine — all actions call the provided callbacks.
 *
 * Accessibility: role="dialog" with labelled title/body. MODAL by default (spec
 * §2.2): focus is trapped in the coachmark (the blocking scrim makes the rest of
 * the page non-interactive), so the user must use the coachmark's own controls,
 * click the highlighted target, or exit. ESC and Skip always exit and skip the
 * WHOLE tour (persisted, never re-fires) — a focus-trap must have a way out.
 * Click-outside does NOT dismiss by default (the scrim swallows it); a step may
 * opt in via `dismissOnOutsideClick` (then an outside click dismisses just that
 * step), and `modal: false` opts out of the trap entirely.
 */

import { Button } from '@/components/ui/button'
import {
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import type { Placement } from '@floating-ui/react'
import { Sparkles } from 'lucide-react'
import { type CSSProperties, useCallback, useEffect, useId, useRef, useState } from 'react'
import type { ActiveFtueStep, FtueCta } from '../types'
import { findScrollableAncestor, usePrefersReducedMotion } from './hooks'

// Matches the in-app coach strip: sky-50 fill (kept lightly translucent over the
// dimmed page, with backdrop-blur) and a soft sky-200 outline.
const COACH_BG = 'rgba(240, 249, 255, 0.92)'
const COACH_BORDER = '#BAE6FD'

// Primary action buttons use the system blue (matching the in-app blue CTAs)
// rather than the default near-black `Button` fill.
const COACH_BUTTON = 'bg-blue-600 text-white hover:bg-blue-700'

// Exit "pop" duration (spec §9: quick 120-150ms, no bounce). On any leave action
// the card plays this before the engine swaps/removes it, so an advance reads as
// "exit current, then pop next" rather than a hard cut.
const EXIT_MS = 140

// transform-origin for the pop, derived from the RESOLVED floating-ui placement
// (post-flip) so the card scales out from the caret/anchor side — it reads as
// emerging from the highlighted element. The card sits on the side named by the
// placement, with its caret on the opposite edge (e.g. placement `bottom` => card
// below the anchor, caret on top => grow from the top edge).
function popOrigin(placement: Placement): string {
  const side = placement.split('-')[0]
  switch (side) {
    case 'top':
      return 'center bottom'
    case 'left':
      return 'right center'
    case 'right':
      return 'left center'
    default:
      return 'center top'
  }
}

// Edge-aligned coachmarks can be much narrower than their anchor. In that case
// FloatingArrow tries to keep pointing at the anchor's centre and gets clamped
// into the card's rounded corner, where it looks clipped/broken. Keep the caret
// inset on the aligned edge instead; this also points at the content that the
// `-start` / `-end` placement was chosen to teach.
function arrowInsetStyle(placement: Placement): CSSProperties | undefined {
  const [side, alignment] = placement.split('-')
  if (!alignment) return undefined
  const horizontal = side === 'top' || side === 'bottom'
  if (alignment === 'start') return horizontal ? { left: 24 } : { top: 24 }
  return horizontal ? { right: 24 } : { bottom: 24 }
}

export function Coachmark({ active }: { active: ActiveFtueStep }) {
  const { step, element, layer, index, total } = active
  const { content } = step
  const { primaryCta, secondaryCta } = content
  // Surface-specific copy: the detail overlay shows a richer breakdown than the
  // list card, so a concept-based step can teach to what's actually on screen.
  const body = layer === 'overlay' && content.overlayBody ? content.overlayBody : content.body
  const titleId = useId()
  const bodyId = useId()
  const arrowRef = useRef<SVGSVGElement>(null)

  // Keep latest callbacks in a ref so floating-ui handlers / effects don't
  // re-subscribe every render (a fresh `active` object arrives each frame).
  const activeRef = useRef(active)
  activeRef.current = active

  // On an overlay surface (the job-detail drawer) a step can opt into a different
  // placement — e.g. `left`, so a tall analysis section can't push it off-screen.
  const placement = (layer === 'overlay' && step.overlayPlacement) || step.placement || 'bottom'

  // Modal by default: trap focus in the coachmark (the blocking scrim makes the
  // rest of the page non-interactive). Always paired with an exit (ESC/Skip).
  const modal = step.modal ?? true

  // Single-shot pop motion (spec §9). On any leave action, play a quick exit
  // (scale 1 -> 0.96 + fade) on the inner wrapper, THEN run the engine callback —
  // which swaps/removes this coachmark, so an advance reads as "exit then pop the
  // next". `prefers-reduced-motion` skips the animation and runs the callback
  // immediately (no defer), so reduced-motion never delays the flow. A ref guards
  // re-entry and a fallback timer guarantees the callback fires even if the
  // element is gone before any transition would end.
  const reducedMotion = usePrefersReducedMotion()
  const [exiting, setExiting] = useState(false)
  const exitingRef = useRef(false)
  const exitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Holds the deferred leave callback while the exit pop plays, so we can flush it
  // if this coachmark unmounts before the timer fires. Without this, a
  // `dismissOnAction` step whose anchor click ALSO navigates away (the Screen-3
  // scan-handoff sidebar entry) loses the race: navigation detaches the anchor,
  // the host unmounts this coachmark, the cleanup clears the timer, and the
  // deferred `onComplete` never runs — so the step is never persisted and the
  // coachmark re-fires forever on every return to /resume.
  const pendingLeave = useRef<(() => void) | null>(null)
  const leaveThen = useCallback(
    (fn: () => void) => {
      if (exitingRef.current) return
      if (reducedMotion) {
        fn()
        return
      }
      exitingRef.current = true
      setExiting(true)
      pendingLeave.current = fn
      exitTimer.current = setTimeout(() => {
        pendingLeave.current = null
        fn()
      }, EXIT_MS)
    },
    [reducedMotion],
  )
  useEffect(
    () => () => {
      clearTimeout(exitTimer.current)
      // Unmounted mid-exit (e.g. the anchor navigated away): run the still-pending
      // leave action now so a deferred complete/dismiss always persists.
      const fn = pendingLeave.current
      pendingLeave.current = null
      fn?.()
    },
    [],
  )

  const {
    refs,
    floatingStyles,
    context,
    placement: resolvedPlacement,
  } = useFloating({
    open: true,
    // `fixed` puts the popover in the SAME viewport coordinate space the Spotlight
    // cutout uses (it positions from getBoundingClientRect + position:fixed). With
    // the default `absolute` strategy, a portal-to-body popover is positioned via
    // its offset parent + document scroll, which drifts from the anchor when the
    // anchor lives inside an internal scroll container (the Opportunities list is
    // in `h-full overflow-auto`): the spotlight stayed correct while the coachmark
    // slid off by the scroll offset, worsening for anchors further down the list.
    strategy: 'fixed',
    placement,
    elements: { reference: element },
    onOpenChange: (_open, _event, reason) => {
      // A focus-trapped modal coachmark must have an exit. ESC skips the WHOLE
      // tour (decision: one exit dismisses the entire FTUE, persisted server-side
      // so it can't re-fire). An outside press only reaches here when a step opts
      // into `dismissOnOutsideClick`, and keeps the legacy single-step dismiss.
      // Both play the exit pop first (see `leaveThen`).
      if (reason === 'escape-key') leaveThen(() => activeRef.current.onSkipAll())
      else leaveThen(() => activeRef.current.onDismiss())
    },
    middleware: [
      offset(10),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    // `animationFrame: true` re-measures every frame so the popover tracks the
    // anchor through the page-entrance keyframe animation (`animate-page-in`),
    // which moves the anchor via a CSS transform that scroll/resize listeners
    // never see. Matches the Spotlight's per-frame tracking (see useAnchorRect).
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
  })

  // Action-gated by default: an outside click must NOT skip the step (only the
  // step's actions advance it). ESC stays enabled for keyboard accessibility.
  const dismiss = useDismiss(context, {
    outsidePress: step.dismissOnOutsideClick ?? false,
    escapeKey: true,
  })
  const role = useRole(context, { role: 'dialog' })
  const { getFloatingProps } = useInteractions([dismiss, role])

  // Dismiss-on-action: interacting with the anchored target completes the step
  // (after the exit pop).
  useEffect(() => {
    if (!step.dismissOnAction || !element) return
    const handler = () => leaveThen(() => activeRef.current.onComplete())
    element.addEventListener('pointerdown', handler, { once: true })
    return () => element.removeEventListener('pointerdown', handler)
  }, [step.dismissOnAction, element, leaveThen])

  // Scroll-lock (opt-in, overlay-only): freeze the anchored element's nearest
  // scrollable ancestor so the page behind the mask can't scroll out from under
  // the spotlight. Captures the prior inline overflow and restores it on cleanup
  // (advance / skip / tour exit, and on a layer flip — the host remounts us), so
  // nothing is ever left locked.
  useEffect(() => {
    if (!step.lockScroll || layer !== 'overlay' || !element) return
    const container = findScrollableAncestor(element)
    if (!container) return
    // Capture the prior inline overflow on both axes and restore exactly that —
    // never a hardcoded value, so a class-driven `overflow-y-auto` falls back to
    // the stylesheet on restore. Setting both longhands is equivalent to
    // `overflow: hidden` and avoids shorthand-expansion edge cases.
    const prevX = container.style.overflowX
    const prevY = container.style.overflowY
    container.style.overflowX = 'hidden'
    container.style.overflowY = 'hidden'
    return () => {
      container.style.overflowX = prevX
      container.style.overflowY = prevY
    }
  }, [step.lockScroll, layer, element])

  // Advance-on-spotlight-click (opt-in, overlay-only): a click anywhere in the
  // spotlighted area advances to the next step, so the whole highlight is a
  // click-to-continue target. Overlay-only so the list keeps its tap-to-open
  // behaviour (where clicking the fit region opens the overlay, not advances).
  useEffect(() => {
    if (!step.advanceOnSpotlightClick || layer !== 'overlay' || !element) return
    const handler = () => leaveThen(() => activeRef.current.onNext())
    element.addEventListener('pointerdown', handler, { once: true })
    return () => element.removeEventListener('pointerdown', handler)
  }, [step.advanceOnSpotlightClick, layer, element, leaveThen])

  // Exit-tour-on-anchor-click (opt-in, any layer): interacting with the anchored
  // target exits the WHOLE tour instead of advancing — for a highlight that
  // navigates away from the coachmark's context (e.g. the Learn "Resource Library"
  // button scrolls to an in-page section), where there is no reliable surface left
  // to anchor the next step. Goes through `skipAll` so the flow is marked seen and
  // never re-fires.
  useEffect(() => {
    if (!step.exitTourOnAnchorClick || !element) return
    const handler = () => leaveThen(() => activeRef.current.onSkipAll())
    element.addEventListener('pointerdown', handler, { once: true })
    return () => element.removeEventListener('pointerdown', handler)
  }, [step.exitTourOnAnchorClick, element, leaveThen])

  if (!element) return null

  // Every advance/dismiss goes through `leaveThen` so the card plays its exit pop
  // before the engine swaps/removes it.
  const leaveComplete = () => leaveThen(() => activeRef.current.onComplete())
  const leaveNext = () => leaveThen(() => activeRef.current.onNext())
  const leaveBack = () => leaveThen(() => activeRef.current.onBack())
  const leaveSkip = () => leaveThen(() => activeRef.current.onSkipAll())

  const runCta = (cta: FtueCta) => {
    // Side effect runs immediately (before the exit + action), per the contract.
    cta.onSelect?.()
    switch (cta.action) {
      case 'complete':
        leaveComplete()
        break
      case 'next':
        leaveNext()
        break
      case 'skip':
        leaveSkip()
        break
      case 'navigate':
        if (cta.href) {
          const href = cta.href
          leaveThen(() => activeRef.current.onCtaNavigate(href))
        }
        break
    }
  }

  // Inner-wrapper pop: scale/opacity only, animated on an element floating-ui does
  // NOT touch, so its outer `translate` (positioning + per-frame anchor tracking)
  // is never clobbered. Entrance is the CSS keyframe (reduced-motion drops it via
  // `motion-safe`); exit is an inline transition while `exiting`.
  const innerStyle: CSSProperties = {
    background: COACH_BG,
    borderColor: COACH_BORDER,
    transformOrigin: popOrigin(resolvedPlacement),
    ...(exiting
      ? {
          transform: 'scale(0.96)',
          opacity: 0,
          transition: `transform ${EXIT_MS}ms ease-in, opacity ${EXIT_MS}ms ease-in`,
          animation: 'none',
        }
      : null),
  }
  const innerClassName = `relative rounded-xl border text-foreground shadow-lg backdrop-blur-sm${
    exiting ? '' : ' motion-safe:animate-coachmark-pop'
  }`

  const isMultiStep = total > 1
  const isLastStep = index === total - 1
  // "Next" advances within a multi-step tour; the final step has nothing after it,
  // so it shows its dismiss label ("Got it") instead.
  const showNext = isMultiStep && !primaryCta && !isLastStep
  // When the only way forward is interacting with the anchored target, we render
  // no Got-it button (the body tells the user what to click); the Skip exit is
  // still shown so the focus-trapped coachmark always has a reachable control.
  const completesViaAnchor = step.dismissOnAction ?? false
  // The auto "Got it" ack applies when there are no CTAs and the step isn't purely
  // anchor-driven — OR when it sets an explicit `dismissLabel` even though clicking
  // the anchor also completes it (e.g. the score card: any button inside dismisses
  // the coachmark, but a "Got it" ack is still offered).
  const gotItApplies =
    !primaryCta && !secondaryCta && (!completesViaAnchor || !!content.dismissLabel)
  // Render it unless the step opts out via `hideDismiss` (a spotlight-only step
  // advanced by an external action, e.g. the upload step). ESC still exits.
  const showGotIt = gotItApplies && !step.hideDismiss
  // The standalone Skip exit shows on a single-concept coachmark UNLESS the ack
  // already gives the focus-trap a control (an info/ack step), or the step opts
  // out via `hideSkip`. ESC always exits regardless, so the trap is never without
  // an escape.
  const showSkip = !gotItApplies && !step.hideSkip

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={modal} returnFocus>
        {/* Outer element: floating-ui owns it (position + `translate` + anchor
            tracking). It carries NO scale/opacity transform — only the inner
            wrapper animates, so the pop never fights the positioning transform. */}
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          // biome-ignore lint/a11y/useSemanticElements: floating popover, not a modal <dialog> element
          role="dialog"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          className="z-[70] w-[240px] max-w-[calc(100vw-24px)]"
          {...getFloatingProps()}
        >
          {/* Inner wrapper: the visible card + the single-shot pop (scale/opacity),
              with transform-origin toward the caret so it emerges from the anchor. */}
          <div className={innerClassName} style={innerStyle}>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              style={arrowInsetStyle(resolvedPlacement)}
              fill={COACH_BG}
              stroke={COACH_BORDER}
              strokeWidth={1}
            />

            {/* Header — constant across all coachmarks. */}
            <div className="flex items-center gap-1.5 px-3 pt-2 pb-1.5">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-sky-500" />
              <h3 id={titleId} className="text-xs font-medium tracking-tight text-slate-700">
                Pelgo Coach
              </h3>
            </div>
            <div className="border-t" style={{ borderColor: COACH_BORDER }} />

            {/* Body — contextual, fully rendered from the first frame (no typing). */}
            <div className="px-3 py-2.5 text-center">
              <p id={bodyId} className="min-h-[1rem] text-xs leading-snug text-foreground/80">
                {body}
              </p>
            </div>

            {/* Actions — contextual. */}
            {isMultiStep ? (
              <div className="flex items-center justify-between gap-2 px-3 pt-0.5 pb-2.5">
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className="tabular-nums">
                    {index + 1} of {total}
                  </span>
                  <button
                    type="button"
                    onClick={leaveBack}
                    disabled={index === 0}
                    className="rounded px-1.5 py-0.5 transition-colors hover:bg-sky-100/60 disabled:opacity-40"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={leaveSkip}
                    className="rounded px-1.5 py-0.5 transition-colors hover:bg-sky-100/60"
                  >
                    Skip
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {secondaryCta && (
                    <button
                      type="button"
                      onClick={() => runCta(secondaryCta)}
                      className="text-xs font-medium text-sky-600 transition-colors hover:text-sky-700"
                    >
                      {secondaryCta.label}
                    </button>
                  )}
                  {primaryCta ? (
                    <Button
                      size="sm"
                      className={`h-7 text-xs ${COACH_BUTTON}`}
                      onClick={() => runCta(primaryCta)}
                    >
                      {primaryCta.label}
                    </Button>
                  ) : showNext ? (
                    <Button size="sm" className={`h-7 text-xs ${COACH_BUTTON}`} onClick={leaveNext}>
                      Next
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className={`h-7 text-xs ${COACH_BUTTON}`}
                      onClick={leaveComplete}
                    >
                      {content.dismissLabel ?? 'Got it'}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5 px-3 pt-0.5 pb-3">
                {primaryCta && (
                  <Button
                    size="sm"
                    className={`h-7 w-full text-xs ${COACH_BUTTON}`}
                    onClick={() => runCta(primaryCta)}
                  >
                    {primaryCta.label}
                  </Button>
                )}
                {secondaryCta && (
                  <button
                    type="button"
                    onClick={() => runCta(secondaryCta)}
                    className="text-xs font-medium text-sky-600 transition-colors hover:text-sky-700"
                  >
                    {secondaryCta.label}
                  </button>
                )}
                {showGotIt && (
                  <Button
                    size="sm"
                    className={`h-7 text-xs ${COACH_BUTTON}`}
                    onClick={leaveComplete}
                  >
                    {content.dismissLabel ?? 'Got it'}
                  </Button>
                )}
                {/* Standalone Skip exit. Suppressed when "Got it" already gives the
                    focus-trap a control, or when the step opts out via `hideSkip`
                    (e.g. the scan handoff, advanced by clicking the highlight). ESC
                    still exits and skips the whole tour in every case. */}
                {showSkip && (
                  <button
                    type="button"
                    onClick={leaveSkip}
                    className="rounded px-1.5 py-0.5 text-[10px] text-slate-500 transition-colors hover:bg-sky-100/60 hover:text-slate-700"
                  >
                    Skip
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}
